import { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import _ from 'lodash';
import { Icon, IconButton, Box, Flex, List, ListItem, ListItemIcon, Divider, Text, Tooltip, Dropdown, Badge } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { type ColumnSettings as ColumnSettingsType } from '@gorilla/portal-shared/src/schemas';
import { Board } from '@gorilla/common/src/lib/monday-api/api';
import { BOARD_COLUMNS_META } from '@gorilla/portal-shared/src/consts';
import { ColumnConfigModal, hasConfigOptionsSet } from './ColumnConfigModal';
import { ColumnVisibilityModal } from './ColumnVisibilityModal';
import { ExternalLink } from './ExternalLink';
import {
  Hide as HideIcon,
  Show as ShowIcon,
  Settings as SettingsIcon,
  Alert as AlertIcon,
  Sort as SortIcon,
  Locked,
  Unlocked,
  Drag,
  Status as StatusIcon,
  Info,
} from '@vibe/icons';

const COLUMN_ORDER_OPTIONS = [
  { label: 'Default', value: 'board' },
  { label: 'Custom', value: 'manual' },
] as const;

type SortingMode = 'board' | 'manual';

type MetaSettings = {
  sortingMode?: SortingMode;
  columnsOrdered?: string[];
};

function cleanupColumnSettings(board: Board, columnSettings: ColumnSettingsType) {
  const columnSettingsCopy = { ...columnSettings };

  for (const columnId in columnSettingsCopy) {
    if (columnId === '___meta___') continue;

    const column = board.columns.find((boardColumn) => boardColumn.id === columnId);

    if (!column) {
      delete columnSettingsCopy[columnId];
    } else {
      columnSettingsCopy[columnId].type = column.type;
    }
  }

  return columnSettingsCopy;
}

function getMetaSettings(columnSettings: ColumnSettingsType): MetaSettings {
  return (_.get(columnSettings, '___meta___') || {}) as MetaSettings;
}

function withMeta(columnSettings: ColumnSettingsType, nextMeta: MetaSettings): ColumnSettingsType {
  return {
    ...columnSettings,
    ___meta___: {
      ...getMetaSettings(columnSettings),
      ...nextMeta,
    },
  } as ColumnSettingsType;
}

type ColumnSettingsProps = {
  board: Board;
  columnSettings: ColumnSettingsType;
  showEditItemValueButton: boolean;
  onChange: (columnSettings: ColumnSettingsType) => void;
  screenMode: 'small' | 'medium' | 'large';
  isDisplayOrderAutoWidth?: boolean;
  mode?: 'grid' | 'table' | 'clientitem';
  showScopeDropdown?: boolean;
  showAggregateButton?: boolean;
};

type SortableColumnItemProps = {
  columnId: string;
  index: number;
  columnIds: string[];
  columnsById: Record<string, any>;
  columnSettings: ColumnSettingsType;
  board: Board;
  showEditItemValueButton: boolean;
  mode?: 'grid' | 'table' | 'clientitem';
  metaSettings: MetaSettings;
  columnIdsWithEditSupport: string[];
  setColumnIdModal: (columnId: string) => void;
  setColumnIdVisibilityModal: (columnId: string) => void;
  onChange: (columnSettings: ColumnSettingsType) => void;
  showScopeDropdown?: boolean;
  showAggregateButton?: boolean;
};

function SortableColumnItem({
  columnId,
  index,
  columnIds,
  columnsById,
  columnSettings,
  board,
  showEditItemValueButton,
  mode,
  metaSettings,
  columnIdsWithEditSupport,
  setColumnIdModal,
  setColumnIdVisibilityModal,
  onChange,
  showScopeDropdown,
  showAggregateButton,
}: SortableColumnItemProps) {
  const column = columnsById[columnId];
  const isVisible = _.get(columnSettings, [column.id, 'visible']) ? true : false;
  const isEditable = _.get(columnSettings, [column.id, 'editable']) ? true : false;
  const isSupportedColumnType = _.get(BOARD_COLUMNS_META, [column.type]) && !_.get(BOARD_COLUMNS_META, [column.type, 'unsupported']);
  const hasEditSupport = columnIdsWithEditSupport.includes(column.id);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    disabled: metaSettings?.sortingMode !== 'manual',
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : undefined,
  };

  let listIcon = null;

  if (column.type === 'name' && !isVisible) {
    listIcon = (
      <Tooltip
        content="The item name may be displayed in some places in the client portal even if it's set to hidden."
        position="right"
        showOnDialogEnter={false}
        zIndex={10000}
      >
        <ListItemIcon icon={Info} className="custom__listitemicon" />
      </Tooltip>
    );
  } else if (column.type === 'mirror' && isVisible) {
    listIcon = (
      <ExternalLink
        href="https://getgorilla.app/products/client-portal/performance"
        target="_blank"
        style={{ marginLeft: '-4px', color: '#323338' }}
      >
        <Tooltip
          title="Warning"
          content="Mirror columns may slow loading performance within your portal. Click to learn more."
          position="right"
          showOnDialogEnter={false}
          zIndex={10000}
        >
          <ListItemIcon icon={AlertIcon} className="custom__listitemicon" />
        </Tooltip>
      </ExternalLink>
    );
  } else if (column.type === 'formula' && isVisible) {
    listIcon = (
      <ExternalLink
        href="https://getgorilla.app/products/client-portal/formulas"
        target="_blank"
        style={{ marginLeft: '-4px', color: '#323338' }}
      >
        <Tooltip
          title="Warning"
          content="Formula columns have usage limits and may not always show values. Click to learn more."
          position="right"
          showOnDialogEnter={false}
          zIndex={10000}
        >
          <ListItemIcon icon={AlertIcon} className="custom__listitemicon" />
        </Tooltip>
      </ExternalLink>
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ListItem disabled={!isVisible} size="medium" className="portalmanager__content__listitem portalmanager__content__listitem--noclick">
        {isSupportedColumnType ? (
          <div style={{ marginRight: '12px' }}>
            <IconButton
              icon={isVisible ? ShowIcon : HideIcon}
              kind={isVisible ? 'primary' : !isSupportedColumnType ? 'tertiary' : 'secondary'}
              color="primary"
              size="xs"
              disabled={!isSupportedColumnType}
              onClick={() => {
                onChange(
                  cleanupColumnSettings(board, {
                    ...columnSettings,
                    [column.id]: {
                      ...(columnSettings[column.id] || {}),
                      visible: !isVisible,
                    },
                  } as ColumnSettingsType),
                );
              }}
            />
          </div>
        ) : (
          <div style={{ marginRight: '12px' }}>
            <Tooltip content="Column not supported" position="right" showOnDialogEnter={false}>
              <IconButton icon={isVisible ? ShowIcon : HideIcon} kind="secondary" color="primary" size="xs" disabled onClick={() => {}} />
            </Tooltip>
          </div>
        )}

        <Flex
          gap={12}
          style={{
            maxWidth: '70%',
          }}
        >
          <Text
            type="text2"
            color={isVisible ? 'primary' : 'secondary'}
            ellipsis
            style={{
              opacity: isVisible ? 1 : 0.5,
            }}
          >
            {column.title}
          </Text>

          {<>{listIcon}</>}
        </Flex>

        <Box style={{ overflow: 'visible', marginLeft: 'auto' }}>
          <Flex gap={8}>
            {showEditItemValueButton && hasEditSupport ? (
              <div>
                <IconButton
                  icon={isEditable ? Unlocked : Locked}
                  kind={isEditable ? 'primary' : !hasEditSupport ? 'tertiary' : 'secondary'}
                  color="primary"
                  size="xs"
                  disabled={!hasEditSupport || !isVisible}
                  onClick={() => {
                    onChange(
                      cleanupColumnSettings(board, {
                        ...columnSettings,
                        [column.id]: {
                          ...(columnSettings[column.id] || {}),
                          editable: !isEditable,
                        },
                      } as ColumnSettingsType),
                    );
                  }}
                  tooltipContent={!isVisible ? '' : isEditable ? 'Editable by clients' : 'Read-only for clients'}
                  tooltipProps={{ position: 'left', showOnDialogEnter: false, hideWhenReferenceHidden: true }}
                />
              </div>
            ) : null}

            {showScopeDropdown ? (
              columnSettings[column.id]?.scope !== undefined && columnSettings[column.id]?.scope !== 'overview-detail' ? (
                <Badge
                  // @ts-ignore
                  color="primary"
                  className="column_settings--badge"
                >
                  <IconButton
                    icon={StatusIcon}
                    kind={isSupportedColumnType ? 'secondary' : 'tertiary'}
                    size="xs"
                    onClick={() => {
                      setColumnIdVisibilityModal(column.id);
                    }}
                    disabled={!isSupportedColumnType || !isVisible ? true : false}
                    tooltipContent={
                      isSupportedColumnType && isVisible && columnSettings[column.id]?.scope === undefined
                        ? 'Set column visibility'
                        : undefined
                    }
                    tooltipProps={{ position: 'left', showOnDialogEnter: false, hideWhenReferenceHidden: true }}
                  />
                </Badge>
              ) : (
                <IconButton
                  icon={StatusIcon}
                  kind={isSupportedColumnType ? 'secondary' : 'tertiary'}
                  size="xs"
                  onClick={() => {
                    setColumnIdVisibilityModal(column.id);
                  }}
                  disabled={!isSupportedColumnType || !isVisible ? true : false}
                  tooltipContent={isSupportedColumnType && isVisible ? 'Set column visibility' : undefined}
                  tooltipProps={{ position: 'left', showOnDialogEnter: false, hideWhenReferenceHidden: true }}
                />
              )
            ) : null}

            {hasConfigOptionsSet(column, columnSettings[column.id]?.config || {}, mode) ||
            (showAggregateButton && column.type === 'numbers' && !!columnSettings[column.id]?.aggregate) ? (
              <Badge
                // @ts-ignore
                color="primary"
                className="column_settings--badge"
              >
                <IconButton
                  icon={SettingsIcon}
                  kind="secondary"
                  size="xs"
                  onClick={() => {
                    setColumnIdModal(column.id);
                  }}
                  disabled={!isSupportedColumnType}
                />
              </Badge>
            ) : (
              <IconButton
                icon={SettingsIcon}
                kind="tertiary"
                size="xs"
                onClick={() => {
                  setColumnIdModal(column.id);
                }}
                disabled={!isSupportedColumnType}
              />
            )}

            {metaSettings?.sortingMode === 'manual' && columnIds.length > 1 ? (
              <Flex gap={0} align="stretch">
                <div style={{ marginRight: '14px', marginLeft: '12px' }}>
                  <Divider direction="vertical" withoutMargin />
                </div>

                <div
                  {...attributes}
                  {...listeners}
                  style={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '24px',
                    height: '24px',
                  }}
                >
                  <Icon icon={Drag} size={18} className="column__drag__icon" />
                </div>
              </Flex>
            ) : null}
          </Flex>
        </Box>
      </ListItem>

      {index !== columnIds.length - 1 && <Divider withoutMargin />}
    </div>
  );
}

export function ColumnSettings({
  board,
  columnSettings,
  onChange,
  showEditItemValueButton,
  screenMode,
  isDisplayOrderAutoWidth,
  mode,
  showScopeDropdown,
  showAggregateButton,
}: ColumnSettingsProps) {
  const [columnIdVisibilityModal, setColumnIdVisibilityModal] = useState<string | null>(null);
  const selectedVisibilityColumn = columnIdVisibilityModal ? board.columns.find((column) => column.id === columnIdVisibilityModal) : null;

  const [columnIdModal, setColumnIdModal] = useState<null | string>();
  const selectedColumn = columnIdModal ? board.columns.find((column) => column.id === columnIdModal) : null;
  const selectedColumnSettings = columnIdModal ? columnSettings[columnIdModal] : null;
  const metaSettings = getMetaSettings(columnSettings);

  const columnsById = _.keyBy(board.columns, 'id');

  const columnIdsOrderedByMainBoard = board.columns
    .filter((column) => !_.get(BOARD_COLUMNS_META, [column.type, 'ignore']))
    .map((column) => column.id);

  const columnIdsOrderedByUser = (metaSettings.columnsOrdered ? [...metaSettings.columnsOrdered] : []).filter((columnId) =>
    columnIdsOrderedByMainBoard.includes(columnId),
  );

  const missingColumns = columnIdsOrderedByMainBoard.filter((columnId) => !columnIdsOrderedByUser.includes(columnId));
  columnIdsOrderedByUser.push(...missingColumns);

  const columnIdsWithEditSupport = board.columns
    .filter((column) => _.get(BOARD_COLUMNS_META, [column.type, 'editable']))
    .map((column) => column.id);

  const columnIds = metaSettings.sortingMode === 'manual' ? columnIdsOrderedByUser : columnIdsOrderedByMainBoard;

  const [localColumnIds, setLocalColumnIds] = useState<string[]>(columnIds);

  useEffect(() => {
    setLocalColumnIds(columnIds);
  }, [columnIds.join('|')]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localColumnIds.findIndex((id) => id === active.id);
    const newIndex = localColumnIds.findIndex((id) => id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newColumnIds = arrayMove(localColumnIds, oldIndex, newIndex);

    setLocalColumnIds(newColumnIds);

    onChange(
      cleanupColumnSettings(
        board,
        withMeta(columnSettings, {
          sortingMode: 'manual',
          columnsOrdered: newColumnIds,
        }),
      ),
    );
  }

  const selectedColumnOrderOption =
    COLUMN_ORDER_OPTIONS.find((option) => option.value === metaSettings.sortingMode) || COLUMN_ORDER_OPTIONS[0];

  const supportedColumnIds = board.columns
    .filter((column) => !_.get(BOARD_COLUMNS_META, [column.type, 'ignore']))
    .filter((column) => _.get(BOARD_COLUMNS_META, [column.type]) && !_.get(BOARD_COLUMNS_META, [column.type, 'unsupported']))
    .map((column) => column.id);

  const allSupportedVisible = supportedColumnIds.length > 0 && supportedColumnIds.every((id) => _.get(columnSettings, [id, 'visible']));

  const setAllVisible = (nextVisible: boolean) => {
    const nextSettings: ColumnSettingsType = { ...columnSettings };

    for (const columnId of supportedColumnIds) {
      nextSettings[columnId] = {
        ...(nextSettings[columnId] || {}),
        visible: nextVisible,
      };
    }

    onChange(cleanupColumnSettings(board, nextSettings));
  };

  const toggleAllVisible = () => {
    const anyHidden = supportedColumnIds.some((id) => !_.get(columnSettings, [id, 'visible']));
    setAllVisible(anyHidden);
  };

  return (
    <>
      {selectedColumn ? (
        <ColumnConfigModal
          show={true}
          column={selectedColumn}
          mode={mode}
          columnConfig={selectedColumnSettings?.config || {}}
          aggregate={selectedColumnSettings?.aggregate}
          showAggregate={showAggregateButton}
          onClose={(result) => {
            if (result) {
              onChange(
                cleanupColumnSettings(board, {
                  ...columnSettings,
                  [selectedColumn.id]: {
                    ...(selectedColumnSettings || {}),
                    config: result.config,
                    aggregate: result.aggregate || undefined,
                  },
                }),
              );
            }
            setColumnIdModal(null);
          }}
        />
      ) : null}
      {selectedVisibilityColumn ? (
        <ColumnVisibilityModal
          show={true}
          column={selectedVisibilityColumn}
          columnSettings={columnSettings}
          onClose={(newColumnSettings) => {
            if (newColumnSettings !== null && newColumnSettings !== undefined) {
              onChange(
                cleanupColumnSettings(board, {
                  ...columnSettings,
                  [selectedVisibilityColumn.id]: {
                    ...(columnSettings[selectedVisibilityColumn.id] || {}),
                    ...newColumnSettings,
                  },
                }),
              );
            }

            setColumnIdVisibilityModal(null);
          }}
        />
      ) : null}
      <div>
        <Flex
          gap={0}
          align="stretch"
          style={{
            width: '100%',
          }}
        >
          {screenMode !== 'small' && screenMode !== 'medium' ? (
            <>
              <Box
                padding="medium"
                backgroundColor="primaryBackgroundColor"
                style={{
                  width: '24px',
                }}
              >
                <Flex style={{ height: '100%' }} align="center" justify="center">
                  <IconButton
                    icon={allSupportedVisible ? HideIcon : ShowIcon}
                    kind="tertiary"
                    color="primary"
                    size="xs"
                    onClick={toggleAllVisible}
                    tooltipContent={allSupportedVisible ? 'Hide all columns' : 'Show all columns'}
                    tooltipProps={{ position: 'left', showOnDialogEnter: false }}
                  />
                </Flex>
              </Box>

              {isDisplayOrderAutoWidth ? (
                <div>
                  <Divider direction="vertical" withoutMargin />
                </div>
              ) : null}
            </>
          ) : null}

          <div
            style={{
              flex: 1,
              marginLeft: screenMode === 'medium' || screenMode === 'small' ? '0' : isDisplayOrderAutoWidth ? '0px' : 'calc(50% - 57px)',
              width: screenMode === 'medium' || screenMode === 'small' ? '100%' : isDisplayOrderAutoWidth ? 'auto' : 'calc(50% - 57px)',
            }}
          >
            <Box padding="medium" backgroundColor="primaryBackgroundColor">
              <Flex gap={32}>
                <Flex gap={8}>
                  <div style={{ fontSize: '12px', transform: 'translate3d(0,1px,0)' }}>
                    <Icon icon={SortIcon} size={18} />
                  </div>
                  <Text type="text2" weight="medium" color="primary">
                    Display order
                  </Text>
                </Flex>

                <div
                  style={{
                    marginLeft: 'auto',
                    width: '110px',
                  }}
                >
                  <Dropdown
                    placeholder="Column order"
                    size="small"
                    className="custom_dropdown"
                    options={COLUMN_ORDER_OPTIONS as unknown as any[]}
                    value={dropdownValue(selectedColumnOrderOption) as unknown as any}
                    clearable={false}
                    searchable={false}
                    onChange={(selectedOption: any) => {
                      onChange(
                        cleanupColumnSettings(
                          board,
                          withMeta(columnSettings, {
                            sortingMode: selectedOption.value as SortingMode,
                          }),
                        ),
                      );
                    }}
                  />
                </div>
              </Flex>
            </Box>
          </div>
        </Flex>

        <Divider withoutMargin />

        <Flex
          direction="column"
          align="start"
          gap={0}
          style={{
            width: '100%',
          }}
        >
          <Box
            style={{
              overflowY: 'auto',
              width: '100%',
              maxHeight: '315px',
              backgroundColor: '#fff',
              boxShadow: localColumnIds.length > 5 ? 'inset 0 4px 6px -4px rgba(0, 0, 0, 0.1)' : 'none',
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={localColumnIds} strategy={verticalListSortingStrategy}>
                <List>
                  {localColumnIds.map((columnId, index) => (
                    <SortableColumnItem
                      key={columnId}
                      columnId={columnId}
                      index={index}
                      columnIds={localColumnIds}
                      columnsById={columnsById}
                      columnSettings={columnSettings}
                      board={board}
                      showEditItemValueButton={showEditItemValueButton}
                      mode={mode}
                      metaSettings={metaSettings}
                      columnIdsWithEditSupport={columnIdsWithEditSupport}
                      showScopeDropdown={showScopeDropdown}
                      showAggregateButton={showAggregateButton}
                      setColumnIdModal={setColumnIdModal}
                      setColumnIdVisibilityModal={setColumnIdVisibilityModal}
                      onChange={onChange}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          </Box>
        </Flex>
      </div>
    </>
  );
}
